import React from 'react';
import styled from 'styled-components';
import { Plus, Trash2 } from 'lucide-react';
import CollectionsSchedulesEditor, {
  collectionsSchedulesPayload,
  emptyCollectionsSchedules,
  validateCollectionsSchedules,
  type CollectionsSchedulesDraft,
} from '../components/CollectionsSchedulesEditor';
import { businessLabel } from '../presentation/contractPresentation';
import ContractSchedulesEditor, {
  contractSchedulesPayload,
  emptyContractSchedules,
  validateContractSchedules,
  type ContractSchedulesDraft,
} from '../components/ContractSchedulesEditor';

export interface ManualEligibilityRuleDraft {
  ruleName: string;
  ruleType: 'ROW' | 'ROW_OPTIONAL' | 'ADVANCE_RATE'
    | 'CONCENTRATION_OBLIGOR' | 'CONCENTRATION_STATE'
    | 'CONCENTRATION_VINTAGE' | 'CONCENTRATION_COUNTRY';
  fieldName: string;
  operator: string;
  thresholdValue: string;
  thresholdText: string;
  advanceRatePct: string;
  sourceClause: string;
}

export interface ManualCovenantDraft {
  covenantKey: string;
  covenantName: string;
  thresholdType: 'MIN' | 'MAX';
  thresholdValue: string;
  formulaExpression: string;
  reportingFrequency: string;
  sourceClause: string;
}

/**
 * A material term the maker states the agreement is silent on. Held in the value
 * so one control answers the term either way; the payload sends it as a decision
 * (`termsNotStated`), never as a value.
 */
export const NOT_STATED = '\u2014not stated\u2014';
export const isNotStated = (value: string | undefined) => value === NOT_STATED;

/** One contract term as the maker states it, with the clause it comes from. */
export interface ManualTermDraft {
  value: string;
  sourceClause: string;
}

export interface ManualAuthorityDraft {
  authorityReference: string;
  termSourceClause: string;
  effectiveFrom: string;
  /** Keyed by canonical term key. A term left blank is not entered, not "not stated". */
  terms: Record<string, ManualTermDraft>;
  eligibilityRules: ManualEligibilityRuleDraft[];
  covenants: ManualCovenantDraft[];
  noFinancialCovenants: boolean;
  // Accounts and payment calendar typed from the agreement (no PDF needed).
  collections?: CollectionsSchedulesDraft;
  // Waterfall, lender classes and consequences, likewise (FR-1.5).
  schedules?: ContractSchedulesDraft;
}

export const emptyManualAuthority = (): ManualAuthorityDraft => ({
  authorityReference: '',
  termSourceClause: '',
  effectiveFrom: '',
  terms: {},
  eligibilityRules: [{
    ruleName: '', ruleType: 'ROW', fieldName: '', operator: 'LTE',
    thresholdValue: '', thresholdText: '', advanceRatePct: '', sourceClause: '',
  }],
  covenants: [{
    covenantKey: '', covenantName: '', thresholdType: 'MIN', thresholdValue: '',
    formulaExpression: '', reportingFrequency: '', sourceClause: '',
  }],
  noFinancialCovenants: false,
  collections: emptyCollectionsSchedules(),
  schedules: emptyContractSchedules(),
});

export const normalizeEffectiveDate = (value: string): string | null => {
  const trimmed = value.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  const parts = iso
    ? { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) }
    : us
      ? { year: Number(us[3]), month: Number(us[1]), day: Number(us[2]) }
      : null;
  if (!parts) return null;
  const parsed = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  if (parsed.getUTCFullYear() !== parts.year
    || parsed.getUTCMonth() + 1 !== parts.month
    || parsed.getUTCDate() !== parts.day) return null;
  return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
};

export type TermKind = 'text' | 'number' | 'integer' | 'date' | 'select';

export interface TermField {
  key: string;
  kind: TermKind;
  options?: string[];
  hint?: string;
}

/**
 * The canonical contract terms, grouped the way the review page groups them.
 * Identity (facility code, external reference) is not here: it belongs to the
 * facility record, not to a clause a checker verifies.
 * A term left blank keeps whatever the facility record already holds; a term
 * typed here is the maker's assertion, cited to its own clause.
 */
export const TERM_SECTIONS: Array<{ name: string; open: boolean; fields: TermField[] }> = [
  {
    name: 'Capacity and collateral support', open: true, fields: [
      { key: 'totalCommitment', kind: 'number' },
      { key: 'drawnAmount', kind: 'number', hint: 'Advanced at closing' },
      { key: 'advanceRatePct', kind: 'number', hint: '0-100' },
      { key: 'reserveAmount', kind: 'number' },
    ],
  },
  {
    name: 'Pricing', open: true, fields: [
      { key: 'financingIndex', kind: 'select', options: ['SOFR', 'TERM_SOFR', 'PRIME', 'LIBOR', 'FIXED'] },
      { key: 'spreadBps', kind: 'integer' },
      { key: 'rateFloorPct', kind: 'number' },
      { key: 'allInRatePct', kind: 'number', hint: 'Only if the agreement states one' },
      { key: 'unusedFeeBps', kind: 'integer' },
      { key: 'tickingFeeBps', kind: 'integer', hint: 'Delayed-draw facilities only: fee on undrawn commitment during ramp-up' },
      { key: 'dayCount', kind: 'select', options: ['ACT_360', 'ACT_365', 'ACT_ACT', '30_360'] },
    ],
  },
  {
    name: 'Facility structure', open: true, fields: [
      { key: 'commitmentType', kind: 'select', options: ['REVOLVING', 'DELAYED_DRAW', 'TERM'], hint: 'Revolving unless the agreement says otherwise' },
      { key: 'reborrowAllowed', kind: 'select', options: ['true', 'false'], hint: 'May repaid amounts be redrawn? Revolving facilities default to yes' },
      { key: 'termOutOption', kind: 'text', hint: 'The term-out option as written, if the agreement grants one' },
    ],
  },
  {
    name: 'Dates', open: true, fields: [
      { key: 'closingDate', kind: 'date' },
      { key: 'maturityDate', kind: 'date' },
      { key: 'commitmentTerminationDate', kind: 'date', hint: 'End of the revolving or reinvestment period' },
      { key: 'amortizationStartDate', kind: 'date', hint: 'Amortization period commencement; the day after the termination date unless stated' },
      { key: 'nextBorrowingBaseDate', kind: 'date', hint: 'First borrowing-base certificate' },
    ],
  },
  {
    name: 'Parties and structure', open: false, fields: [
      { key: 'facilityName', kind: 'text' },
      { key: 'obligorName', kind: 'text' },
      { key: 'sponsorName', kind: 'text' },
      { key: 'originatorName', kind: 'text' },
      { key: 'sellerName', kind: 'text' },
      { key: 'servicerName', kind: 'text' },
      { key: 'backupServicerName', kind: 'text' },
      { key: 'adminAgentName', kind: 'text' },
      { key: 'collateralType', kind: 'select', options: ['CONSUMER_LOANS', 'AUTO_LOANS', 'EQUIPMENT_FINANCE', 'FUND_FINANCE', 'AVIATION', 'ROYALTIES', 'LITIGATION_FINANCE', 'SOLAR_PPA'] },
      { key: 'abfStructure', kind: 'select', options: ['REVOLVER', 'TERM', 'HYBRID'] },
      { key: 'currency', kind: 'select', options: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'] },
    ],
  },
];

export const TERM_FIELDS: TermField[] = TERM_SECTIONS.flatMap(section => section.fields);

export const termDraft = (authority: ManualAuthorityDraft, key: string): ManualTermDraft =>
  authority.terms?.[key] || { value: '', sourceClause: '' };

/** A typed contract value, or the reason it cannot be staged as one. */
export const termValue = (field: TermField, raw: string): { value: unknown } | { problem: string } => {
  const trimmed = raw.trim();
  const label = businessLabel(field.key);
  if (field.kind === 'number' || field.kind === 'integer') {
    const parsed = Number(trimmed.replace(/,/g, ''));
    if (!Number.isFinite(parsed)) return { problem: `${label} must be a number` };
    if (field.kind === 'integer' && !Number.isInteger(parsed)) {
      return { problem: `${label} must be a whole number` };
    }
    if (field.key === 'advanceRatePct' && (parsed < 0 || parsed > 100)) {
      return { problem: `${label} must be between 0 and 100` };
    }
    if (parsed < 0) return { problem: `${label} cannot be negative` };
    return { value: parsed };
  }
  if (field.kind === 'date') {
    const iso = normalizeEffectiveDate(trimmed);
    if (!iso) return { problem: `${label} must be a date as MM/DD/YYYY or YYYY-MM-DD` };
    return { value: iso };
  }
  return { value: trimmed };
};

export const validateManualAuthority = (authority: ManualAuthorityDraft): string | null => {
  const numericFields = new Set([
    'current_balance', 'original_balance', 'current_interest_rate_pct', 'fico_score',
    'ltv_pct', 'dti_pct', 'dscr', 'days_past_due', 'property_value', 'vintage_year',
  ]);
  const concentrationFields: Record<string, string> = {
    CONCENTRATION_OBLIGOR: 'obligor_identifier', CONCENTRATION_STATE: 'state',
    CONCENTRATION_VINTAGE: 'vintage_year', CONCENTRATION_COUNTRY: 'country',
  };
  if (!authority.authorityReference.trim()) return 'Contract authority reference is required';
  if (!authority.termSourceClause.trim()) return 'Contract term source clause is required';
  if (!normalizeEffectiveDate(authority.effectiveFrom)) {
    return 'Enter a valid contract effective date as MM/DD/YYYY or YYYY-MM-DD';
  }
  if (!authority.eligibilityRules.length) return 'At least one eligibility rule is required';
  if (!authority.eligibilityRules.some(rule => ['ROW', 'ROW_OPTIONAL'].includes(rule.ruleType))) {
    return 'At least one row eligibility rule is required';
  }
  for (let index = 0; index < authority.eligibilityRules.length; index += 1) {
    const rule = authority.eligibilityRules[index];
    if (!rule.ruleName.trim() || !rule.fieldName.trim() || !rule.operator
      || (!rule.thresholdValue && !rule.thresholdText.trim()) || !rule.sourceClause.trim()) {
      return `Eligibility rule ${index + 1} is incomplete`;
    }
    if (rule.thresholdValue && rule.thresholdText.trim()) {
      return `Eligibility rule ${index + 1} must use either a numeric or text threshold`;
    }
    const numericThreshold = rule.thresholdValue !== '';
    if (numericFields.has(rule.fieldName) !== numericThreshold) {
      return `Eligibility rule ${index + 1} threshold type does not match its canonical field`;
    }
    const concentrationField = concentrationFields[rule.ruleType];
    if (concentrationField && (rule.fieldName !== concentrationField || rule.operator !== 'LTE'
      || Number(rule.thresholdValue) < 0 || Number(rule.thresholdValue) > 100)) {
      return `Concentration rule ${index + 1} requires its matching field, LTE, and a 0-100 numeric cap`;
    }
    if (!numericFields.has(rule.fieldName) && !['EQ', 'NE'].includes(rule.operator)) {
      return `Text/boolean rule ${index + 1} must use EQ or NE`;
    }
    if (rule.ruleType === 'ADVANCE_RATE' && (!rule.advanceRatePct
      || Number(rule.advanceRatePct) < 0 || Number(rule.advanceRatePct) > 100)) {
      return `Advance-rate rule ${index + 1} requires a 0-100 applied rate`;
    }
  }
  if (!authority.noFinancialCovenants && !authority.covenants.length) {
    return 'Enter the financial covenants or attest that none apply';
  }
  const applicableCovenants = authority.noFinancialCovenants ? [] : authority.covenants;
  for (let index = 0; index < applicableCovenants.length; index += 1) {
    const covenant = applicableCovenants[index];
    if (!covenant.covenantKey.trim() || !covenant.covenantName.trim()
      || !covenant.thresholdValue || !covenant.formulaExpression
      || !covenant.sourceClause.trim()) return `Covenant ${index + 1} is incomplete`;
  }
  for (const field of TERM_FIELDS) {
    const draft = termDraft(authority, field.key);
    // A term recorded as not stated is answered, not valued: it carries no value
    // to parse and travels as a decision instead.
    if (isNotStated(draft.value)) continue;
    const entered = draft.value.trim() !== '';
    if (!entered && draft.sourceClause.trim()) {
      return `${businessLabel(field.key)} has a source clause but no value`;
    }
    if (!entered) continue;
    const parsed = termValue(field, draft.value);
    if ('problem' in parsed) return parsed.problem;
  }
  const schedulesProblem = validateCollectionsSchedules(authority.collections || emptyCollectionsSchedules());
  if (schedulesProblem) return schedulesProblem;
  return validateContractSchedules(authority.schedules || emptyContractSchedules());
};

export const manualAuthorityPayload = (authority: ManualAuthorityDraft) => ({
  authorityReference: authority.authorityReference.trim(),
  termSourceClause: authority.termSourceClause.trim(),
  effectiveFrom: normalizeEffectiveDate(authority.effectiveFrom) || authority.effectiveFrom.trim(),
  eligibilityRules: authority.eligibilityRules.map((rule, index) => ({
    ruleName: rule.ruleName.trim(),
    ruleType: rule.ruleType,
    fieldName: rule.fieldName.trim(),
    operator: rule.operator,
    thresholdValue: rule.thresholdValue === '' ? null : Number(rule.thresholdValue),
    thresholdText: rule.thresholdText.trim() || null,
    advanceRatePct: rule.advanceRatePct === '' ? null : Number(rule.advanceRatePct),
    severity: 'INELIGIBLE',
    priority: (index + 1) * 10,
    sourceReference: authority.authorityReference.trim(),
    sourceClause: rule.sourceClause.trim(),
  })),
  covenants: authority.noFinancialCovenants ? [] : authority.covenants.map(covenant => ({
    covenantKey: covenant.covenantKey.trim(),
    covenantName: covenant.covenantName.trim(),
    thresholdType: covenant.thresholdType,
    thresholdValue: Number(covenant.thresholdValue),
    formulaExpression: covenant.formulaExpression,
    reportingFrequency: covenant.reportingFrequency.trim() || null,
    sourceClause: covenant.sourceClause.trim(),
  })),
  noFinancialCovenants: authority.noFinancialCovenants,
  ...manualTermsPayload(authority),
  ...collectionsSchedulesPayload(authority.collections || emptyCollectionsSchedules()),
  ...contractSchedulesPayload(authority.schedules || emptyContractSchedules()),
});

/**
 * Only the terms the maker actually typed travel, each with the clause it came
 * from. Everything else is left alone: a term nobody entered stays unanswered
 * on the review page rather than being staged as "not stated in agreement".
 */
const manualTermsPayload = (authority: ManualAuthorityDraft) => {
  const termValues: Record<string, unknown> = {};
  const termSources: Record<string, string> = {};
  for (const field of TERM_FIELDS) {
    const draft = termDraft(authority, field.key);
    if (!draft.value.trim() || isNotStated(draft.value)) continue;
    const parsed = termValue(field, draft.value);
    if ('problem' in parsed) continue;
    termValues[field.key] = parsed.value;
    const clause = draft.sourceClause.trim();
    if (clause) termSources[field.key] = clause;
  }
  return { termValues, termSources };
};

const Wrap = styled.section`
  grid-column: 1 / -1;
  margin-top: .8rem;
  padding: .85rem;
  border: 1px solid rgba(56,189,248,.25);
  border-radius: 10px;
  background: rgba(2,132,199,.055);
`;
const Head = styled.div`
  display:flex; justify-content:space-between; gap:1rem; align-items:flex-start;
  h3 { margin:0; color:#e2e8f0; font-size:.86rem; }
  p { margin:.25rem 0 .75rem; color:#94a3b8; font-size:.68rem; line-height:1.45; }
`;
const Grid = styled.div`
  display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:.55rem; align-items:end;
  @media(max-width:760px){grid-template-columns:1fr;}
`;
const Rule = styled.div`
  margin-top:.6rem; padding:.65rem; border:1px solid rgba(148,163,184,.16);
  border-radius:9px; background:rgba(15,23,42,.55);
`;
const Fields = styled.div`
  display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:.45rem; align-items:end;
  @media(max-width:900px){grid-template-columns:repeat(2,minmax(0,1fr));}
`;
const Label = styled.label`
  display:flex; flex-direction:column; gap:.22rem; color:#94a3b8; font-size:.61rem;
  input,select { min-width:0; border:1px solid rgba(148,163,184,.2); border-radius:7px;
    box-sizing:border-box; width:100%; height:2.2rem; padding:.48rem .55rem;
    background:#0f172a; color:#e2e8f0; font-family:inherit; font-size:.68rem; line-height:1.2; }
`;
const Button = styled.button`
  display:inline-flex; gap:.3rem; align-items:center; border:1px solid rgba(56,189,248,.25);
  border-radius:7px; padding:.42rem .55rem; background:rgba(14,165,233,.1); color:#bae6fd;
  font-size:.65rem; cursor:pointer;
`;
const Check = styled.label`
  display:flex; align-items:center; gap:.45rem; margin-top:.7rem; color:#cbd5e1; font-size:.68rem;
`;

const TermGroup = styled.details`
  margin-top:.6rem; padding:.6rem .65rem; border:1px solid rgba(148,163,184,.16);
  border-radius:9px; background:rgba(15,23,42,.55);
  summary { color:#cbd5e1; font-size:.72rem; cursor:pointer; list-style:none;
    display:flex; justify-content:space-between; gap:.6rem; align-items:center; }
  summary::-webkit-details-marker { display:none; }
  summary::after { content:'▾'; color:#64748b; font-size:.6rem; }
  &[open] summary::after { content:'▴'; }
  &[open] summary { margin-bottom:.55rem; }
  span.count { color:#38bdf8; font-size:.6rem; letter-spacing:.04em; text-transform:uppercase; }
`;
const TermCells = styled.div`
  display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:.5rem;
  @media(max-width:900px){grid-template-columns:repeat(2,minmax(0,1fr));}
  @media(max-width:640px){grid-template-columns:1fr;}
`;
const TermCell = styled.div`
  display:flex; flex-direction:column; gap:.25rem;
  input.clause { border:1px solid rgba(148,163,184,.14); border-radius:6px; box-sizing:border-box;
    width:100%; height:1.9rem; padding:.35rem .5rem; background:rgba(2,6,23,.65); color:#94a3b8;
    font-family:inherit; font-size:.61rem; }
  input.clause:focus { color:#e2e8f0; }
`;

export const ELIGIBILITY_FIELDS = [
  'days_past_due', 'asset_status', 'is_defaulted', 'is_charged_off', 'fico_score',
  'ltv_pct', 'dti_pct', 'dscr', 'current_balance', 'original_balance', 'vintage_year',
  'delinquency_bucket', 'state', 'country', 'collateral_bucket', 'coupon_type', 'obligor_identifier',
];
export const COVENANT_FORMULAS = [
  'EXCESS_AVAILABILITY', 'ADVANCE_RATE_PCT', 'DELINQUENCY_PCT', 'NET_LOSS_RATE_PCT',
  'MATCHED_CASH_COVERAGE_PCT', 'EXCESS_SPREAD_PCT', 'LARGEST_STATE_CONCENTRATION_PCT',
];
export const REPORTING_FREQUENCIES = [
  'Daily', 'Weekly', 'Monthly', 'Quarterly', 'Semi-annual', 'Annual', 'Event-driven',
];

interface Props {
  value: ManualAuthorityDraft;
  onChange: (value: ManualAuthorityDraft) => void;
  /** What the facility record already holds, shown as the fallback for each term. */
  knownTerms?: Record<string, unknown>;
}

const ManualAuthorityEditor: React.FC<Props> = ({ value, onChange, knownTerms }) => {
  const updateTerm = (key: string, patch: Partial<ManualTermDraft>) => onChange({
    ...value,
    terms: { ...(value.terms || {}), [key]: { ...termDraft(value, key), ...patch } },
  });
  const knownValue = (key: string): string => {
    const known = knownTerms?.[key];
    if (known == null || known === '') return '';
    return String(known);
  };
  const updateRule = (index: number, patch: Partial<ManualEligibilityRuleDraft>) => onChange({
    ...value,
    eligibilityRules: value.eligibilityRules.map((rule, i) => i === index ? { ...rule, ...patch } : rule),
  });
  const updateCovenant = (index: number, patch: Partial<ManualCovenantDraft>) => onChange({
    ...value,
    covenants: value.covenants.map((covenant, i) => i === index ? { ...covenant, ...patch } : covenant),
  });
  return <Wrap>
    <Head><div><h3>Explicit contract authority</h3><p>Manual entry requires the executed agreement’s actual rules. State each contract term against the clause it comes from; a term left blank keeps the facility record’s value and stays open for review. These values are staged for independent activation; they do not create a trade or collateral authority.</p></div></Head>
    <Grid>
      <Label>Authority reference<input value={value.authorityReference} onChange={e => onChange({ ...value, authorityReference: e.target.value })} placeholder="Executed agreement / approval reference" /></Label>
      <Label>Terms source clause<input value={value.termSourceClause} onChange={e => onChange({ ...value, termSourceClause: e.target.value })} placeholder="Section / schedule covering facility terms" /></Label>
      <Label>Effective date (MM/DD/YYYY)<input type="text" inputMode="numeric" value={value.effectiveFrom} onChange={e => onChange({ ...value, effectiveFrom: e.target.value })} placeholder="06/15/2026" autoComplete="off" /></Label>
    </Grid>
    {TERM_SECTIONS.map(section => {
      const entered = section.fields.filter(field => termDraft(value, field.key).value.trim()).length;
      return <TermGroup key={section.name} open={section.open}>
        <summary>
          <span>{section.name}</span>
          <span className="count">{entered ? `${entered} of ${section.fields.length} entered` : 'None entered'}</span>
        </summary>
        <TermCells>
          {section.fields.map(field => {
            const draft = termDraft(value, field.key);
            const known = knownValue(field.key);
            const placeholder = known ? `On the facility record: ${known}` : field.hint || '';
            return <TermCell key={field.key}>
              <Label>
                {businessLabel(field.key)}
                {field.kind === 'select'
                  ? <select value={draft.value} onChange={e => updateTerm(field.key, { value: e.target.value })}>
                      <option value="">{known ? `Keep ${known}` : 'Not entered'}</option>
                      {(field.options || []).map(option => <option key={option}>{option}</option>)}
                    </select>
                  : <input
                      type={field.kind === 'number' || field.kind === 'integer' ? 'number' : 'text'}
                      step={field.kind === 'integer' ? '1' : field.kind === 'number' ? 'any' : undefined}
                      inputMode={field.kind === 'date' ? 'numeric' : undefined}
                      value={draft.value}
                      placeholder={placeholder}
                      autoComplete="off"
                      onChange={e => updateTerm(field.key, { value: e.target.value })}
                    />}
              </Label>
              <input
                className="clause"
                value={draft.sourceClause}
                placeholder="Clause for this term"
                autoComplete="off"
                onChange={e => updateTerm(field.key, { sourceClause: e.target.value })}
              />
            </TermCell>;
          })}
        </TermCells>
      </TermGroup>;
    })}
    {value.eligibilityRules.map((rule, index) => <Rule key={`rule-${index}`}>
      <Head><div><h3>Eligibility rule {index + 1}</h3></div><Button type="button" onClick={() => onChange({ ...value, eligibilityRules: value.eligibilityRules.filter((_, i) => i !== index) })}><Trash2 size={12}/>Remove</Button></Head>
      <Fields>
        <Label>Rule name<input value={rule.ruleName} onChange={e => updateRule(index, { ruleName: e.target.value })}/></Label>
        <Label>Type<select value={rule.ruleType} onChange={e => updateRule(index, { ruleType: e.target.value as ManualEligibilityRuleDraft['ruleType'] })}><option>ROW</option><option>ROW_OPTIONAL</option><option>ADVANCE_RATE</option><option>CONCENTRATION_OBLIGOR</option><option>CONCENTRATION_STATE</option><option>CONCENTRATION_VINTAGE</option><option>CONCENTRATION_COUNTRY</option></select></Label>
        <Label>Canonical field<select value={rule.fieldName} onChange={e => updateRule(index, { fieldName: e.target.value })}><option value="">Select…</option>{ELIGIBILITY_FIELDS.map(field => <option key={field}>{field}</option>)}</select></Label>
        <Label>Operator<select value={rule.operator} onChange={e => updateRule(index, { operator: e.target.value })}>{['LTE','LT','GTE','GT','EQ','NE'].map(op => <option key={op}>{op}</option>)}</select></Label>
        <Label>Numeric threshold<input type="number" step="any" value={rule.thresholdValue} onChange={e => updateRule(index, { thresholdValue: e.target.value, thresholdText: '' })}/></Label>
        <Label>Text threshold<input value={rule.thresholdText} onChange={e => updateRule(index, { thresholdText: e.target.value, thresholdValue: '' })}/></Label>
        <Label>Applied rate % (matrix only)<input type="number" step="any" value={rule.advanceRatePct} onChange={e => updateRule(index, { advanceRatePct: e.target.value })}/></Label>
        <Label>Source clause<input value={rule.sourceClause} onChange={e => updateRule(index, { sourceClause: e.target.value })}/></Label>
      </Fields>
    </Rule>)}
    <Button type="button" onClick={() => onChange({ ...value, eligibilityRules: [...value.eligibilityRules, { ruleName:'', ruleType:'ROW', fieldName:'', operator:'LTE', thresholdValue:'', thresholdText:'', advanceRatePct:'', sourceClause:'' }] })}><Plus size={12}/>Add eligibility rule</Button>
    <Check><input type="checkbox" checked={value.noFinancialCovenants} onChange={e => onChange({ ...value, noFinancialCovenants: e.target.checked })}/>The executed agreement has no financial covenants to monitor</Check>
    {!value.noFinancialCovenants && value.covenants.map((covenant, index) => <Rule key={`covenant-${index}`}>
      <Head><div><h3>Covenant {index + 1}</h3></div><Button type="button" onClick={() => onChange({ ...value, covenants: value.covenants.filter((_, i) => i !== index) })}><Trash2 size={12}/>Remove</Button></Head>
      <Fields>
        <Label>Key<input value={covenant.covenantKey} onChange={e => updateCovenant(index, { covenantKey: e.target.value })}/></Label>
        <Label>Name<input value={covenant.covenantName} onChange={e => updateCovenant(index, { covenantName: e.target.value })}/></Label>
        <Label>Threshold type<select value={covenant.thresholdType} onChange={e => updateCovenant(index, { thresholdType: e.target.value as 'MIN'|'MAX' })}><option>MIN</option><option>MAX</option></select></Label>
        <Label>Threshold<input type="number" step="any" value={covenant.thresholdValue} onChange={e => updateCovenant(index, { thresholdValue: e.target.value })}/></Label>
        <Label>Formula<select value={covenant.formulaExpression} onChange={e => updateCovenant(index, { formulaExpression: e.target.value })}><option value="">Select…</option>{COVENANT_FORMULAS.map(formula => <option key={formula}>{formula}</option>)}</select></Label>
        <Label>Frequency<select value={covenant.reportingFrequency} onChange={e => updateCovenant(index, { reportingFrequency: e.target.value })}><option value="">Select…</option>{REPORTING_FREQUENCIES.map(frequency => <option key={frequency}>{frequency}</option>)}</select></Label>
        <Label>Source clause<input value={covenant.sourceClause} onChange={e => updateCovenant(index, { sourceClause: e.target.value })}/></Label>
      </Fields>
    </Rule>)}
    {!value.noFinancialCovenants && <Button type="button" onClick={() => onChange({ ...value, covenants: [...value.covenants, { covenantKey:'', covenantName:'', thresholdType:'MIN', thresholdValue:'', formulaExpression:'', reportingFrequency:'', sourceClause:'' }] })}><Plus size={12}/>Add covenant</Button>}
    <CollectionsSchedulesEditor
      value={value.collections || emptyCollectionsSchedules()}
      onChange={collections => onChange({ ...value, collections })}
      effectiveFrom={normalizeEffectiveDate(value.effectiveFrom) || ''}
      authorityHint={value.authorityReference.trim() || undefined}
      idPrefix="manual-authority-collections"
    />
    <div style={{ marginTop: '.6rem' }}>
      <ContractSchedulesEditor
        value={value.schedules || emptyContractSchedules()}
        onChange={schedules => onChange({ ...value, schedules })}
      />
    </div>
  </Wrap>;
};

export default ManualAuthorityEditor;
